import type { CSSProperties, HTMLAttributes } from 'react';

import { Icon, type IconName } from '../core/Icon';

export interface AppHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  readonly brand?: string;
  readonly date?: string;
  readonly markIcon?: IconName;
  readonly style?: CSSProperties;
}

/**
 * En-tête master : la marque est réellement centrée sur toute la largeur du bandeau (grille à 3
 * colonnes `1fr auto 1fr`, pas un simple centrage dans l'espace restant), la date reste alignée à
 * droite. Les contrôles Mode/État ne vivent plus ici — voir `DesktopNav`/`MobileNav` (revue
 * visuelle « corrections visuelles desktop »). Ne porte pas de navigation propre —
 * `DesktopNav`/`MobileNav` en sont l'unique source, pour éviter toute navigation dupliquée dans
 * l'arbre d'accessibilité.
 */
export function AppHeader({
  brand = 'ASCEND INVICTUS',
  date,
  markIcon = 'normal',
  style,
  ...rest
}: AppHeaderProps) {
  return (
    <header
      {...rest}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        columnGap: 16,
        minHeight: 'var(--header-h)',
        padding: '14px 22px',
        background: 'var(--surface-deep)',
        border: '1px solid var(--accent-line)',
        borderRadius: 'var(--radius-panel)',
        boxShadow: 'var(--shadow-card)',
        ...style,
      }}
    >
      <span aria-hidden="true" />
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifySelf: 'center',
          gap: 12,
          color: 'var(--accent)',
        }}
      >
        <Icon name={markIcon} size={20} />
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 19,
            fontWeight: 500,
            letterSpacing: '.13em',
            whiteSpace: 'nowrap',
          }}
        >
          {brand}
        </span>
      </span>
      <span style={{ justifySelf: 'end' }}>
        {date && (
          <span
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 11,
              letterSpacing: '.06em',
              color: 'var(--text-faint)',
              whiteSpace: 'nowrap',
            }}
          >
            {date}
          </span>
        )}
      </span>
    </header>
  );
}
